import { AnimatePresence, motion } from 'framer-motion'
import logoUrl from '@/assets/images/Codefied..svg'

// Full-screen splash shown during route transitions — see RootLayout for the
// navigation interception that drives `show`.
export function PageLoader({ show }: { show: boolean }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="page-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-white"
          aria-live="polite"
          aria-busy={show}
        >
          {/* Drifting brand-color blobs */}
          <motion.div
            className="pointer-events-none absolute -left-24 -top-24 h-[360px] w-[360px] rounded-full blur-[90px]"
            style={{
              background: 'radial-gradient(circle, rgba(212,87,91,0.28) 0%, transparent 70%)',
            }}
            animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="pointer-events-none absolute -bottom-24 -right-24 h-[380px] w-[380px] rounded-full blur-[90px]"
            style={{
              background: 'radial-gradient(circle, rgba(59,130,246,0.26) 0%, transparent 70%)',
            }}
            animate={{ x: [0, -30, 0], y: [0, -40, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px]"
            style={{
              background: 'radial-gradient(circle, rgba(245,166,35,0.18) 0%, transparent 70%)',
            }}
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="relative flex flex-col items-center gap-8">
            {/* Logo — gentle continuous breathing pulse */}
            <motion.img
              src={logoUrl}
              alt="Codefied"
              initial={{ opacity: 0, scale: 0.92, y: 6 }}
              animate={{ opacity: 1, scale: [1, 1.04, 1], y: 0 }}
              transition={{
                opacity: { duration: 0.4, ease: 'easeOut' },
                y: { duration: 0.4, ease: 'easeOut' },
                scale: { duration: 1.6, repeat: Infinity, ease: 'easeInOut', delay: 0.4 },
              }}
              className="h-6 w-auto sm:h-7"
            />

            {/* Progress bar — flowing gradient + shimmer sweep + leading glow */}
            <div className="relative h-[5px] w-[150px] overflow-hidden rounded-full bg-[#EAEAEA] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] sm:w-[170px]">
              <motion.div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #D4575B, #3B82F6, #F5A623, #D4575B)',
                  backgroundSize: '200% 100%',
                }}
                initial={{ width: '0%' }}
                animate={{ width: '100%', backgroundPositionX: ['0%', '100%'] }}
                transition={{
                  width: { duration: 2.3, ease: 'easeInOut' },
                  backgroundPositionX: { duration: 1.1, repeat: Infinity, ease: 'linear' },
                }}
              />
              {/* Glossy shimmer sweeping across the whole track */}
              <motion.div
                className="absolute inset-y-0 left-0 w-1/3 skew-x-[-20deg]"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent)',
                }}
                animate={{ x: ['-120%', '350%'] }}
                transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* Glowing dot leading the fill */}
              <motion.div
                className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-white"
                style={{ boxShadow: '0 0 8px 2px rgba(212,87,91,0.7)' }}
                initial={{ left: '0%' }}
                animate={{ left: ['0%', '100%'] }}
                transition={{ duration: 2.3, ease: 'easeInOut' }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
